// Reactive state store with path-based subscriptions

const defaultState = {
  osc1: { type: 'sawtooth', detune: 0 },
  osc2: { type: 'square', detune: 7 },
  oscMix: 0.5,
  fmEnabled: false,
  fmAmount: 0,

  filter: { cutoff: 5000, resonance: 1, type: 'lowpass' },

  ampEnv: { attack: 0.01, decay: 0.3, sustain: 0.6, release: 0.5 },
  filterEnv: { attack: 0.05, decay: 0.4, sustain: 0.3, release: 0.5, amount: 2000 },

  effects: {
    reverb: { wet: 0.3, decay: 3 },
    delay: { wet: 0.2, time: 0.25, feedback: 0.35 },
    chorus: { wet: 0, frequency: 1.5, depth: 0.7 },
    distortion: { wet: 0, amount: 0.3 },
    phaser: { wet: 0, frequency: 0.5, octaves: 3 },
  },

  arp: { enabled: false, pattern: 'up', rate: '8n', octaves: 2 },

  lfo: { rate: 2, depth: 0, destination: 'filter' },

  master: { volume: 0.7 },

  currentPreset: 'Event Horizon',
};

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function getByPath(obj, path) {
  return path.split('.').reduce((o, k) => (o != null ? o[k] : undefined), obj);
}

function setByPath(obj, path, value) {
  const keys = path.split('.');
  const last = keys.pop();
  const target = keys.reduce((o, k) => {
    if (o[k] == null) o[k] = {};
    return o[k];
  }, obj);
  target[last] = value;
}

export function createState(initial) {
  const data = deepClone(initial || defaultState);
  const subscribers = new Map(); // path -> Set<callback>
  let batchMode = false;
  let batchedPaths = new Set();

  function get(path) {
    if (!path) return deepClone(data);
    return getByPath(data, path);
  }

  function set(path, value) {
    setByPath(data, path, value);
    if (batchMode) {
      batchedPaths.add(path);
    } else {
      notify(path);
    }
  }

  function notify(path) {
    for (const [subPath, callbacks] of subscribers) {
      // Notify if the subscription path is a prefix of or equal to the changed path,
      // or if the changed path is a prefix of the subscription path
      if (path.startsWith(subPath) || subPath.startsWith(path)) {
        for (const cb of callbacks) {
          cb(getByPath(data, subPath), subPath);
        }
      }
    }
  }

  function subscribe(path, callback) {
    if (!subscribers.has(path)) {
      subscribers.set(path, new Set());
    }
    subscribers.get(path).add(callback);
    // Immediately call with current value
    callback(getByPath(data, path), path);
    // Return unsubscribe function
    return () => subscribers.get(path)?.delete(callback);
  }

  function batch(fn) {
    batchMode = true;
    batchedPaths.clear();
    fn();
    batchMode = false;
    // Notify all unique paths
    const notified = new Set();
    for (const path of batchedPaths) {
      if (!notified.has(path)) {
        notified.add(path);
        notify(path);
      }
    }
    batchedPaths.clear();
  }

  function loadPreset(preset) {
    batch(() => {
      for (const [key, value] of Object.entries(preset)) {
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          for (const [subKey, subValue] of Object.entries(value)) {
            if (typeof subValue === 'object' && subValue !== null && !Array.isArray(subValue)) {
              for (const [subSubKey, subSubValue] of Object.entries(subValue)) {
                set(`${key}.${subKey}.${subSubKey}`, subSubValue);
              }
            } else {
              set(`${key}.${subKey}`, subValue);
            }
          }
        } else {
          set(key, value);
        }
      }
    });
  }

  return { get, set, subscribe, batch, loadPreset };
}

export { defaultState };
